import React, { useState } from 'react';
import {
  CButton,
  CFormGroup,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react';
import _t from 'counterpart';
import { extractErrorMessage } from '../../helpers';
import { useMutation, useQuery } from 'react-query';
import { getSalesStatuses, updateCustomersBulk, listSalesPeople } from '../../services/BackendService';
import QueryDataRenderer from '../../components/QueryDataRenderer';
import { ISalesStatus } from '../salesStatus/types';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import CustomSelect, { ISelectOption } from '../../components/CustomSelect';
import { IBulkEditParams, ICustomer, SecurityLevel } from './types';
import { ICustomersFiltersDTO } from '../../sagas/customersSaga';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../helpers/customHooks';
import { Permission, UserRole } from '../../reducers/userReducer';

interface IProps {
  show: boolean;
  onClose: (refetch?: boolean) => void;
  customersFilters: ICustomersFiltersDTO;
  search: string | null | undefined;
  count: number;
  selectedCustomerIds?: Set<number>;
}

const REMOVE_OPTION_VALUE = '__REMOVE__';

const BulkEditModal = ({ show, onClose, customersFilters, search, count, selectedCustomerIds }: IProps) => {
  const [selectedStatus, setSelectedStatus] = useState<ISelectOption | null>();
  const [conversionAgent, setConversionAgent] = useState<ISelectOption | null>();
  const [retentionAgent, setRetentionAgent] = useState<ISelectOption | null>();
  const [securityLevel, setSecurityLevel] = useState<SecurityLevel | null>(null);
  const [isTest, setIsTest] = useState<boolean | null>(null);

  const permissions = useAppSelector((state) => state.user.permissions);
  const roles = useAppSelector((state) => state.user.roles);
  const isSuperAdmin = roles?.includes(UserRole.SuperAdmin);
  const canManageTestCustomers = permissions?.includes(Permission.MANAGE_TEST_CUSTOMERS);

  const selectedCount = selectedCustomerIds ? selectedCustomerIds.size : count;

  const close = (refetch?: boolean) => {
    onClose(refetch);
    setSelectedStatus(null);
    setConversionAgent(null);
    setRetentionAgent(null);
    setSecurityLevel(null);
    setIsTest(null);
  };

  const resetStatus = () => {
    setSelectedStatus(null);
  };

  const resetConversionAgent = () => {
    setConversionAgent(null);
  };

  const handleConversionAgentChange = (sp: any) => {
    setConversionAgent(sp);
  };

  const resetRetentionAgent = () => {
    setRetentionAgent(null);
  };

  const handleRetentionAgentChange = (sp: any) => {
    setRetentionAgent(sp);
  };

  const resetSecurityLevel = () => {
    setSecurityLevel(null);
  };

  const resetIsTest = () => {
    setIsTest(null);
  };

  const updateCustomersMutation = useMutation((params: IBulkEditParams) => updateCustomersBulk(params), {
    onSuccess: (count) => {
      toast.success(_t('customers.modified-x-customers', { count }));
      close(true);
    },
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
  });

  const handleStatusChange = (status: any) => {
    setSelectedStatus(status);
  };

  const salesStatusesQuery = useQuery('sales-statuses', () => getSalesStatuses(), {
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
  });

  const canViewSalesPeople = permissions?.includes(Permission.VIEW_SALES_PEOPLE);

  const salesPeopleQuery = useQuery('sales-people', () => listSalesPeople(), {
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
    enabled: canViewSalesPeople,
  });

  const isLoading = salesStatusesQuery.isLoading;
  const isError = salesStatusesQuery.isError;

  const handleOnRefetch = () => {
    salesStatusesQuery.refetch();
  };

  const statuses = salesStatusesQuery.data?.map((s: ISalesStatus) => {
    return {
      value: s.id,
      label: s.status,
    };
  });

  const salesPeopleOptions = [
    { value: REMOVE_OPTION_VALUE, label: _t('action.remove-agent') },
    ...(salesPeopleQuery.data?.map((sp: ICustomer) => ({
      value: sp.id,
      label: `${sp.firstname} ${sp.lastname}`,
    })) || []),
  ];

  const invokeMutation = () => {
    const isRemovingConversionAgent = conversionAgent?.value === REMOVE_OPTION_VALUE;
    const isRemovingRetentionAgent = retentionAgent?.value === REMOVE_OPTION_VALUE;

    let conversionAgentValue: string | null | undefined = undefined;
    if (isRemovingConversionAgent) {
      conversionAgentValue = null;
    } else if (conversionAgent) {
      conversionAgentValue = conversionAgent.value;
    }

    let retentionAgentValue: string | null | undefined = undefined;
    if (isRemovingRetentionAgent) {
      retentionAgentValue = null;
    } else if (retentionAgent) {
      retentionAgentValue = retentionAgent.value;
    }

    updateCustomersMutation.mutate({
      ...customersFilters,
      search: search ?? undefined,
      selectedStatus: selectedStatus ? Number(selectedStatus.value) : undefined,
      conversionAgent: conversionAgentValue,
      retentionAgent: retentionAgentValue,
      customerIds: selectedCustomerIds ? Array.from(selectedCustomerIds) : undefined,
      securityLevel: securityLevel ?? undefined,
      isTest: isTest ?? undefined,
    });
  };

  return (
    <CModal show={show} onClose={close}>
      <CModalHeader>
        <CModalTitle>{_t('customers.bulk-edit-customers')}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CLabel>{_t('customers.only-selected-attr')}</CLabel>
        <QueryDataRenderer isLoading={isLoading} isError={isError} onRefetch={handleOnRefetch}>
          <CFormGroup>
            <div className="d-flex align-items-center justify-content-between">
              <span>{_t('customers.filters.status')}</span>
              <CButton className="mb-0 p-0 text-danger reset-single" onClick={resetStatus}>
                {_t('action.reset').toUpperCase()}
              </CButton>
            </div>
            <CustomSelect
              value={selectedStatus ?? null}
              options={statuses}
              onChange={handleStatusChange}
              isClearable={false}
              whiteBackground
              placeholder={_t('customers.select-a-status')}
            />
          </CFormGroup>
          {canViewSalesPeople && (
            <>
              <CFormGroup>
                <div className="d-flex align-items-center justify-content-between">
                  <span>{_t('customer.conversion-agent')}</span>
                  <CButton className="mb-0 p-0 text-danger reset-single" onClick={resetConversionAgent}>
                    {_t('action.reset').toUpperCase()}
                  </CButton>
                </div>
                <CustomSelect
                  value={conversionAgent ?? null}
                  options={salesPeopleOptions}
                  onChange={handleConversionAgentChange}
                  isClearable={false}
                  whiteBackground
                  placeholder={_t('customers.actions.select-retention-agent')}
                />
              </CFormGroup>
              <CFormGroup>
                <div className="d-flex align-items-center justify-content-between">
                  <span>{_t('customer.retention-agent')}</span>
                  <CButton className="mb-0 p-0 text-danger reset-single" onClick={resetRetentionAgent}>
                    {_t('action.reset').toUpperCase()}
                  </CButton>
                </div>
                <CustomSelect
                  value={retentionAgent ?? null}
                  options={salesPeopleOptions}
                  onChange={handleRetentionAgentChange}
                  isClearable={false}
                  whiteBackground
                  placeholder={_t('customers.actions.select-retention-agent')}
                />
              </CFormGroup>
            </>
          )}
          {isSuperAdmin && (
            <CFormGroup>
              <div className="d-flex align-items-center justify-content-between">
                <span>{_t('customers.security-level.title')}</span>
                <CButton className="mb-0 p-0 text-danger reset-single" onClick={resetSecurityLevel}>
                  {_t('action.reset').toUpperCase()}
                </CButton>
              </div>
              <div className="d-flex">
                <CButton
                  color={securityLevel === SecurityLevel.Regular ? 'primary' : 'light'}
                  className="mr-2"
                  onClick={() => setSecurityLevel(SecurityLevel.Regular)}
                >
                  {_t('customers.security-level.regular')}
                </CButton>
                <CButton
                  color={securityLevel === SecurityLevel.Sensitive ? 'primary' : 'light'}
                  onClick={() => setSecurityLevel(SecurityLevel.Sensitive)}
                >
                  {_t('customers.security-level.sensitive')}
                </CButton>
              </div>
              <small className="text-muted d-block mt-2">{_t('customers.security-level.description')}</small>
            </CFormGroup>
          )}
          {canManageTestCustomers && (
            <CFormGroup>
              <div className="d-flex align-items-center justify-content-between">
                <span>{_t('customers.test-accounts.title')}</span>
                <CButton className="mb-0 p-0 text-danger reset-single" onClick={resetIsTest}>
                  {_t('action.reset').toUpperCase()}
                </CButton>
              </div>
              <div className="d-flex">
                <CButton color={isTest === true ? 'primary' : 'light'} className="mr-2" onClick={() => setIsTest(true)}>
                  {_t('customers.test-accounts.mark')}
                </CButton>
                <CButton color={isTest === false ? 'primary' : 'light'} onClick={() => setIsTest(false)}>
                  {_t('customers.test-accounts.unmark')}
                </CButton>
              </div>
              <small className="text-muted d-block mt-2">{_t('customers.test-accounts.description')}</small>
            </CFormGroup>
          )}
        </QueryDataRenderer>
      </CModalBody>
      <CModalFooter>
        <ButtonWithLoader
          isLoading={updateCustomersMutation.isLoading}
          onClick={invokeMutation}
          buttonColor="primary"
          spinnerColor="secondary"
          title={_t('customers.modify-customers', { count: selectedCount })}
          className="mr-2"
          style={{ lineHeight: '1' }} // bootstrap class lh-1 doesn't work
          disabled={selectedCount === 0}
        />
        <CButton color="light" variant="outline" onClick={close}>
          {_t('action.cancel')}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default React.memo(BulkEditModal);
